import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { PRODUCT_GRID_VIEW_DEFAULT, PRODUCT_GRID_VIEW_LARGE, type ProductGridView } from '@/lib/product-grid-view';

function DenseGridIcon({ className }: { className?: string }) {
    return (
        <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" className={cn('size-4', className)}>
            <rect x="1" y="1" width="6" height="6" rx="0.5" />
            <rect x="9" y="1" width="6" height="6" rx="0.5" />
            <rect x="1" y="9" width="6" height="6" rx="0.5" />
            <rect x="9" y="9" width="6" height="6" rx="0.5" />
        </svg>
    );
}

function LargeGridIcon({ className }: { className?: string }) {
    return (
        <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" className={cn('size-4', className)}>
            <rect x="1" y="1" width="6.5" height="6.5" rx="0.5" />
            <rect x="8.5" y="1" width="6.5" height="6.5" rx="0.5" />
            <rect x="1" y="8.5" width="6.5" height="6.5" rx="0.5" />
            <rect x="8.5" y="8.5" width="6.5" height="6.5" rx="0.5" />
        </svg>
    );
}

export default function ProductGridViewToggle({
    gridView,
    onGridViewChange,
    className,
}: {
    gridView: ProductGridView;
    onGridViewChange: (gridView: ProductGridView) => void;
    className?: string;
}): ReactElement {
    const { t } = useTranslation('common');

    const options = [
        {
            value: PRODUCT_GRID_VIEW_DEFAULT,
            label: t('productGrid.viewDefault'),
            Icon: DenseGridIcon,
        },
        {
            value: PRODUCT_GRID_VIEW_LARGE,
            label: t('productGrid.viewLarge'),
            Icon: LargeGridIcon,
        },
    ] as const;

    return (
        <div
            role="group"
            aria-label={t('productGrid.viewToggleLabel')}
            className={cn('inline-flex shrink-0 overflow-hidden rounded-md border', className)}>
            {options.map(({ value, label, Icon }, index) => {
                const isActive = gridView === value;

                return (
                    <button
                        key={value}
                        type="button"
                        aria-label={label}
                        aria-pressed={isActive}
                        onClick={() => onGridViewChange(value)}
                        className={cn(
                            'inline-flex size-9 items-center justify-center transition-colors',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                            isActive
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-background text-foreground hover:bg-accent',
                            index > 0 && 'border-l'
                        )}>
                        <Icon />
                    </button>
                );
            })}
        </div>
    );
}
